import { lazy } from 'react';

/**
 * `React.lazy()` wrapper that survives stale chunks after a redeploy.
 *
 * Vite emits content-hashed chunk filenames, so every deploy invalidates
 * the previous build's chunk names. A tab still running an older build that
 * navigates to a not-yet-loaded lazy route asks the server for a chunk that
 * no longer exists → "Failed to fetch dynamically imported module" → the
 * error boundary turns it into the crash page.
 *
 * On that failure we reload ONCE — the fresh index.html points at the new
 * chunk graph, so the route loads cleanly. A sessionStorage guard prevents
 * an infinite reload loop when the chunk is genuinely missing (a real
 * build/deploy gap), in which case we rethrow so the error boundary still
 * surfaces it. sessionStorage is required because the flag must survive the
 * full page reload (mirrors the pattern already used in apiEnv).
 */
const RELOAD_GUARD_KEY = 'shaka_chunk_reload_attempted';

type LazyFactory = Parameters<typeof lazy>[0];

export function lazyWithReload(factory: LazyFactory) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Clean import → clear the guard so a future stale chunk can reload too.
      try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* storage unavailable */ }
      return mod;
    } catch (err) {
      let alreadyTried = true;
      try {
        alreadyTried = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
        if (!alreadyTried) sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
      } catch {
        alreadyTried = true; // no storage → don't risk a reload loop
      }
      if (!alreadyTried) {
        window.location.reload();
        // Never resolve: the reload navigates away before React renders.
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });
}
